import { ReactElement, ReactNode } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { Alert, Button, Stack, SxProps, Theme, Typography } from '@mui/material'
import { FC90KTextIcon, KC90TextIcon } from '@ui/icons/misc'
import { CheckSettingsFormValues } from '../../MultiEditCheckSettings/types'
import {
  SUPPORT_COMPRESSION_FIELDS,
  SharedSupportCompressionSettings,
  SupportCompressionField,
  getFirstSupportFieldValue,
  supportCompressionFieldLabels,
  wouldLeaveSupportWithoutAreaAndOverhang,
} from '../utils'

/** Styled block headers; the shared warning falls back to the plain-text labels. */
const FIELD_LABELS: Record<SupportCompressionField, ReactNode> = {
  area: supportCompressionFieldLabels.area,
  overhang: supportCompressionFieldLabels.overhang,
  k_c_90: <KC90TextIcon />,
  f_c_90_k: <FC90KTextIcon />,
}

/** Matches the row's `spacing={1.5}` (MUI's default 8px unit × 1.5). */
const ROW_GAP_PX = 12

/**
 * Per-field flex basis of a block — below it the block wraps onto the next
 * line. Area (its two fields stacked) and kc,90 (a single dropdown) stay
 * deliberately narrow so fc,90,k still fits on the same line at ordinary
 * drawer widths.
 */
const FIELD_BASIS_PX: Record<SupportCompressionField, number> = {
  area: 84,
  overhang: 132,
  k_c_90: 68,
  f_c_90_k: 110,
}

/**
 * One field block of the wrapping row: shares the line's width equally with
 * its neighbors down to its basis, below which it wraps onto the next line,
 * and draws its own vertical divider centered in the column gap to its left.
 * The row's `overflow: hidden` clips that divider off the first block of every
 * line, so wrapping never strands a divider at a line start — a `Stack
 * divider` element would wrap as a flex item of its own and do exactly that.
 */
const fieldBlockSx = (basisPx: number): SxProps<Theme> => ({
  flex: `1 1 ${basisPx}px`,
  minWidth: 0,
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: -(ROW_GAP_PX / 2) - 0.5,
    borderLeft: '1px solid',
    borderColor: 'divider',
  },
})

/** German list join for the warning: „A, B und C". */
const formatFieldLabelList = (fields: SupportCompressionField[]): string => {
  const labels = fields.map(field => supportCompressionFieldLabels[field])
  if (labels.length === 1) return labels[0]
  return `${labels.slice(0, -1).join(', ')} und ${labels[labels.length - 1]}`
}

interface Props {
  /** The editor for one field, rendered once that field is staged. */
  fieldEditors: Record<SupportCompressionField, ReactNode>
}

/**
 * Shows every support field side by side, wrapping onto further lines where
 * the row gets too narrow. A field is editable once every support of every
 * selected member agrees on it (or „Ändern" staged the first member's first
 * support value); until then its block offers „Ändern" and one shared warning
 * below the row names the still-differing fields. A staged null that would
 * strip some support of both area and overhang warns here too, right below
 * the fields that caused it (the save button in the actions row stays
 * disabled on the same condition).
 */
const SupportCompressionFields = ({ fieldEditors }: Props): ReactElement => {
  const { getValues, setValue } =
    useFormContext<CheckSettingsFormValues<SharedSupportCompressionSettings>>()

  const sharedSettings = useWatch<CheckSettingsFormValues<SharedSupportCompressionSettings>>({
    name: 'sharedSettings',
  }) as SharedSupportCompressionSettings | undefined
  const elements = useWatch<CheckSettingsFormValues<SharedSupportCompressionSettings>>({
    name: 'elements',
  }) as CheckSettingsFormValues['elements']

  const unstagedFields = SUPPORT_COMPRESSION_FIELDS.filter(
    field => sharedSettings?.[field] === undefined,
  )
  const leavesBareSupport = wouldLeaveSupportWithoutAreaAndOverhang(elements, sharedSettings)

  const handleStageFirstSelection = (field: SupportCompressionField) => {
    const elements = getValues('elements')
    const seed = getFirstSupportFieldValue(elements, field, getValues('sharedSettings'))
    if (seed === undefined) return
    setValue(`sharedSettings.${field}`, seed, { shouldDirty: true, shouldTouch: true })
  }

  return (
    <Stack direction="column" spacing={1} pt={0.5}>
      <Stack direction="row" spacing={1.5} useFlexGap flexWrap="wrap" overflow="hidden">
        {SUPPORT_COMPRESSION_FIELDS.map(field => (
          <Stack
            key={field}
            direction="column"
            spacing={0.75}
            sx={fieldBlockSx(FIELD_BASIS_PX[field])}
          >
            <Typography component="div" variant="caption" color="text.secondary" fontWeight={500}>
              {FIELD_LABELS[field]}
            </Typography>
            {sharedSettings?.[field] !== undefined ? (
              fieldEditors[field]
            ) : (
              <Button
                variant="outlined"
                color="primary"
                size="small"
                onClick={() => handleStageFirstSelection(field)}
                data-cy={`support-compression-change-button-${field}`}
              >
                Ändern
              </Button>
            )}
          </Stack>
        ))}
      </Stack>

      {leavesBareSupport && (
        <Alert severity="warning" data-cy="support-compression-bare-support-warning">
          Mindestens eine der folgenden Angaben ist je Auflager erforderlich: Auflagerfläche oder
          Überstand
        </Alert>
      )}

      {unstagedFields.length > 0 && (
        <Alert severity="warning" data-cy="support-compression-differing-values-alert">
          {`Auflager haben unterschiedliche Werte für ${formatFieldLabelList(unstagedFields)}`}
        </Alert>
      )}
    </Stack>
  )
}

export default SupportCompressionFields
